import {
    authenticatedApi,
    ApiError,
    API_BASE_URL
} from './apiUtils';

export const projectsApi = {
  getAllProjects: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/projects`);
    } catch (error) {
      throw new ApiError('Failed to fetch projects', error.status || 500);
    }
  },

  getProjectsLite: async (employeeId) => {
    try {
      const response = await authenticatedApi.get(`${API_BASE_URL}/projects-lite${employeeId ? `?employeeId=${employeeId}` : ''}`);
      // The API returns the array directly, so we can just return it
      return response;
    } catch (error) {
      console.error('Error in getProjectsLite:', error);
      throw new ApiError('Failed to fetch lite projects', error.status || 500);
    }
  },

  getProjectEmployees: async (projectId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/projects/${projectId}/employees`);
    } catch (error) {
      throw new ApiError('Failed to fetch project employees', error.status || 500);
    }
  },

  attachEmployee: async (projectId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/projects/${projectId}/employees`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee data', 400);
      }
      throw new ApiError('Failed to attach employee to project', error.status || 500);
    }
  },

  createProject: async (projectData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/projects`, projectData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(
          'Validation failed: ' + error.data?.details?.join(', '),
          error.status || 400
        );
      }
      throw new ApiError('Failed to create project', error.status || 500);
    }
  },

  updateProject: async (projectId, projectData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/projects/${projectId}`, projectData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid project data', 400);
      }
      throw new ApiError('Failed to update project', error.status || 500);
    }
  },

  deleteProject: async (projectId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/projects/${projectId}`);
    } catch (error) {
      throw new ApiError('Failed to delete project', error.status || 500);
    }
  },

  addLeafToProject: async (projectId, data) => {
    try {
      // Support both old signature (leafSegmentId string) and new signature (object with leafSegmentId or customSegment)
      const payload = typeof data === 'string' ? { leafSegmentId: data } : data;
      return await authenticatedApi.post(`${API_BASE_URL}/projects/${projectId}/leaves`, payload);
    } catch (error) {
      throw new ApiError('Failed to add leaf to project', error.status || 500);
    }
  },

  removeLeafFromProject: async (projectId, segmentId, options = {}) => {
    try {
      const { isCustomSegment = false } = options;
      // Build payload with either leafSegmentId or customSegmentId based on segment type
      const payload = isCustomSegment
        ? { customSegmentId: segmentId }
        : { leafSegmentId: segmentId };
      return await authenticatedApi.put(`${API_BASE_URL}/projects/${projectId}/leaves`, payload);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to remove leaf from project', error.status || 500, error.data);
    }
  },

  getProjectById: async (projectId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/projects/${projectId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch project by ID', error.status || 500);
    }
  },

  getProjectsByLeafId: async (leafSegmentId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/projects/by-leaf/${leafSegmentId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch projects by leaf segment', error.status || 500);
    }
  },

  updateEmployeeProjectEngagement: async (employeeProjectId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/engagement`,
        data
      );
    } catch (error) {
      throw new ApiError('Failed to update employee project engagement', error.status || 500);
    }
  },

  updateEmployeeProjectRoles: async (employeeProjectId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/roles`,
        data
      );
    } catch (error) {
      throw new ApiError('Failed to update employee project roles', error.status || 500);
    }
  },

  endEmployeeProject: async (employeeProjectId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/end`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid end employee project data', 400);
      }
      throw new ApiError('Failed to end employee project', error.status || 500);
    }
  },

  transferEmployee: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/projects/transfer-employee`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid transfer data', 400);
      }
      throw new ApiError('Failed to transfer employee', error.status || 500);
    }
  },

  // Project Purposes endpoints
  getProjectPurpose: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/project-purposes/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Project Purpose not found', 404);
      }
      throw new ApiError('Failed to fetch project purpose', error.status || 500);
    }
  },

  getProjectPurposes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/project-purposes`);
    } catch (error) {
      throw new ApiError('Failed to fetch project purposes', error.status || 500);
    }
  },

  getProjectPurposesByOrgUnitType: async (orgUnitTypeId) => {
    try {
      if (!orgUnitTypeId) {
        throw new ApiError('orgUnitTypeId is required', 400);
      }
      return await authenticatedApi.get(`${API_BASE_URL}/project-purposes/by-org-unit-type/${orgUnitTypeId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'orgUnitTypeId is required', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Organization unit type not found', 404);
      }
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      throw new ApiError('Failed to fetch project purposes by organization unit type', error.status || 500);
    }
  },

  createProjectPurpose: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/project-purposes`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Project Purpose with this name already exists', 400);
      }
      throw new ApiError('Failed to create project purpose', error.status || 500);
    }
  },

  updateProjectPurpose: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/project-purposes/${id}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Project Purpose not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Project Purpose with this name already exists', 400);
      }
      throw new ApiError('Failed to update project purpose', error.status || 500);
    }
  },

  deleteProjectPurpose: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/project-purposes/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete Project Purpose', 400);
      }
      throw new ApiError('Failed to delete project purpose', error.status || 500);
    }
  },

  // Project Type Policy endpoints
  getAllProjectTypePolicies: async (params) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.orgUnitTypeId) queryParams.append('orgUnitTypeId', params.orgUnitTypeId);
      if (params?.projectPurposeId) queryParams.append('projectPurposeId', params.projectPurposeId);
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/project-type-policies${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch project type policies', error.status || 500);
    }
  },

  getProjectTypePolicyById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/project-type-policies/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Project Type Policy not found', 404);
      }
      throw new ApiError('Failed to fetch project type policy', error.status || 500);
    }
  },

  createProjectTypePolicy: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/project-type-policies`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid project type policy data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Project Type Policy not found', 404);
      }
      throw new ApiError('Failed to create project type policy', error.status || 500);
    }
  },

  updateProjectTypePolicy: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/project-type-policies/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid project type policy data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Project Type Policy not found', 404);
      }
      throw new ApiError('Failed to update project type policy', error.status || 500);
    }
  },

  deleteProjectTypePolicy: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/project-type-policies/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete Project Type Policy', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Project Type Policy not found', 404);
      }
      throw new ApiError('Failed to delete project type policy', error.status || 500);
    }
  },

  overrideEmployeeManager: async (employeeId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employees/${employeeId}/override-manager`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid override manager data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to override employee manager', error.status || 500);
    }
  },

  // Future Assignment endpoints
  canCancelFutureAssignment: async (employeeId, projectId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/future-assignment/can-cancel?employeeId=${employeeId}&projectId=${projectId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid parameters', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee or project not found', 404);
      }
      throw new ApiError('Failed to check future assignment cancellation eligibility', error.status || 500);
    }
  },

  cancelFutureAssignment: async (employeeId, projectId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/future-assignment/cancel`, {
        employeeId,
        projectId
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid future assignment cancellation data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee or future assignment not found', 404);
      }
      throw new ApiError('Failed to cancel future assignment', error.status || 500);
    }
  },

  // Project Leave endpoints
  canCancelProjectLeave: async (employeeId, projectId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/current-leaving/can-cancel?employeeId=${employeeId}&projectId=${projectId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid parameters', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee or project not found', 404);
      }
      throw new ApiError('Failed to check project leave cancellation eligibility', error.status || 500);
    }
  },

  cancelProjectLeave: async (employeeId, projectId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/current-leaving/cancel`, {
        employeeId,
        projectId
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid project leave cancellation data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee or project leave not found', 404);
      }
      throw new ApiError('Failed to cancel project leave', error.status || 500);
    }
  },

  getProjectManagerHistory: async (projectId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/projects/${projectId}/manager-history`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Project not found', 404);
      }
      throw new ApiError('Failed to fetch project manager history', error.status || 500);
    }
  },
};